/* Pulse Creative Scroll — name/CTA tracker for the scroll-driven
   collection picker.

   Each collection's image is a separate full-viewport block stacked
   vertically; the user scrolls through them naturally. We measure each
   image's distance to the viewport center and pick the closest one as
   "active." The matching name brightens, the CTA's href updates.

   Listeners are gated by an IntersectionObserver: scroll work runs only
   while the section is in/near the viewport, so the rest of the page
   doesn't pay any cost. */
(function () {
  function setup(section) {
    if (!section || section.dataset.bound === 'true') return;
    section.dataset.bound = 'true';

    var images = section.querySelectorAll('.pulse-creative-scroll__image');
    var names = section.querySelectorAll('.pulse-creative-scroll__name');
    var cta = section.querySelector('[data-cta]');
    if (!images.length || !names.length) return;

    var lastIdx = -1;
    var ticking = false;
    var inView = false;

    function setActive(idx) {
      if (idx === lastIdx) return;
      lastIdx = idx;
      for (var n = 0; n < names.length; n++) {
        var isActive = n === idx;
        names[n].classList.toggle('is-active', isActive);
        if (isActive) {
          names[n].setAttribute('aria-current', 'true');
        } else {
          names[n].removeAttribute('aria-current');
        }
      }
      if (cta) {
        var url = names[idx].getAttribute('data-url');
        if (url) cta.setAttribute('href', url);
      }
    }

    function update() {
      var viewportCenter = window.innerHeight / 2;
      var bestIdx = 0;
      var bestDist = Infinity;
      for (var i = 0; i < images.length; i++) {
        var rect = images[i].getBoundingClientRect();
        // Skip blocks that are nowhere near the viewport (e.g., section is
        // far above or below). They can't be "active" and reading their
        // rect mid-loop is cheap enough that an early-out isn't critical,
        // but it lets us skip setActive churn.
        if (rect.bottom < 0 || rect.top > window.innerHeight * 2) continue;
        var imgCenter = rect.top + rect.height / 2;
        var dist = Math.abs(imgCenter - viewportCenter);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      }
      setActive(bestIdx);
    }

    function onScroll() {
      if (!inView || ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var k = 0; k < entries.length; k++) {
          inView = entries[k].isIntersecting;
          if (inView) update();
        }
      /* Tight rootMargin — only run scroll work while the section is
         actually intersecting the viewport. The previous 50% padding made
         the listener active well before/after the section was visible,
         which on mobile contributed to perceived scroll friction. */
      }, { rootMargin: '0px' });
      io.observe(section);
    } else {
      inView = true;
    }

    /* Two-tap interaction on names:
         • click an inactive name → smooth-scroll to its image banner
           (the scroll listener then promotes that name to active).
         • click the already-active name → navigate to its collection URL.
       Keyboard parity: Enter/Space mirror click. */
    function handleNameActivate(idx, el) {
      if (el.classList.contains('is-active')) {
        var url = el.getAttribute('data-url');
        if (url) window.location.href = url;
        return;
      }
      var image = images[idx];
      if (!image) return;
      var rect = image.getBoundingClientRect();
      // Aligning the image's top edge to the viewport top puts the image
      // center at viewport center (each image is exactly one viewport
      // tall), which is the same condition the scroll listener uses to
      // mark a name active — so by the time the smooth scroll lands, the
      // active class will have toggled to this name automatically.
      var target = window.scrollY + rect.top;
      window.scrollTo({ top: target, behavior: 'smooth' });
    }

    for (var n = 0; n < names.length; n++) {
      (function (idx, el) {
        /* Distinguish a real tap from a swipe-that-happens-to-end-on-the-name.
           Mobile browsers still fire click after touchend if movement was
           under ~10px — without this guard, a small vertical flick across a
           name triggered our scrollTo handler and overrode the user's natural
           scroll inertia, which felt like the page was "fighting" them.
           If the touch moved more than 8px in either axis, we mark it as a
           swipe and skip the click action. */
        var touchStartX = 0;
        var touchStartY = 0;
        var touchMoved = false;
        el.addEventListener('touchstart', function (e) {
          var t = e.touches[0];
          touchStartX = t.clientX;
          touchStartY = t.clientY;
          touchMoved = false;
        }, { passive: true });
        el.addEventListener('touchmove', function (e) {
          var t = e.touches[0];
          if (Math.abs(t.clientX - touchStartX) > 8 || Math.abs(t.clientY - touchStartY) > 8) {
            touchMoved = true;
          }
        }, { passive: true });
        el.addEventListener('click', function () {
          if (touchMoved) { touchMoved = false; return; }
          handleNameActivate(idx, el);
        });
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            handleNameActivate(idx, el);
          }
        });
      })(n, names[n]);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Initial sync — covers reload-mid-scroll and editor previews where
    // the section is already on screen at mount.
    update();
  }

  function init(root) {
    var sections = (root || document).querySelectorAll('[data-section-type="pulse-creative-scroll"]');
    for (var i = 0; i < sections.length; i++) setup(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
